import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Search, Loader2, Skull, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 24;

export default function Monsters() {
  const { lang, t, dir } = useLang();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [article, setArticle] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [facets, setFacets] = useState([]);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => { setSkip(0); }, [debounced, article, lang]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const p = new URLSearchParams({ lang, limit: PAGE, skip });
      if (debounced) p.set("search", debounced);
      if (article) p.set("article", article);
      const { data: d } = await axios.get(`${API}/monsters?${p}`);
      setData((prev) => ({ ...d, items: skip === 0 ? d.items : [...prev.items, ...d.items] }));
      if (skip === 0 && d.facets?.length) setFacets(d.facets);
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, [lang, skip, debounced, article]);

  useEffect(() => { load(); }, [load]);

  const countLabel = `${data.total} ${data.total === 1 ? t("common.result") : t("common.results")}`;

  return (
    <div className="es-page" data-testid="page-monsters" dir={dir}>
      <Seo
        title="Evony Monsters & Bosses — Power, Levels & Guides | Evony Strategist"
        description="Browse Evony monsters and bosses with power values and the guides that cover them, sourced from the Evony Guide Wiki."
        path="/monsters"
      />
      <header className="es-page-head">
        <h1 className="es-h1"><Skull size={22} className="es-h1-icon" /> {t("monsters.title")}</h1>
        <p className="es-lead">{t("monsters.subtitle")}</p>
      </header>

      <div className="es-search">
        <Search size={18} className="es-search-icon" />
        <input data-testid="monsters-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t("monsters.search")} aria-label={t("monsters.search")} />
      </div>

      {facets.length > 0 && (
        <div className="es-facets" data-testid="monsters-facets">
          <button className={`es-facet ${!article ? "active" : ""}`} onClick={() => setArticle("")} data-testid="monsters-facet-all">{t("common.all")}</button>
          {facets.map((f) => (
            <button key={f.slug} className={`es-facet ${article === f.slug ? "active" : ""}`}
              data-testid={`monsters-facet-${f.slug}`} onClick={() => setArticle(article === f.slug ? "" : f.slug)}>
              {f.title} <span className="es-facet-count">{f.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="es-result-count" data-testid="monsters-count">{countLabel}</div>

      {error ? (
        <div className="es-state" data-testid="monsters-error">{t("common.networkError")}
          <button className="es-btn-ghost" onClick={load}>{t("common.retry")}</button></div>
      ) : data.items.length === 0 && !loading ? (
        <div className="es-state" data-testid="monsters-empty">{t("common.noResults")}</div>
      ) : (
        <div className="es-card-grid" data-testid="monsters-grid">
          {data.items.map((m) => (
            <Link key={m.slug} to={`/monsters/${m.slug}`} className="es-entity-card" data-testid={`monster-card-${m.slug}`}>
              <div className="es-entity-head">
                <span className="es-entity-name">{m.localizedName || m.name}</span>
                <ChevronRight size={16} className="es-entity-chev" />
              </div>
              <div className="es-entity-stats">
                {m.power != null && <span className="es-stat"><span className="es-stat-l">{t("monsters.power")}</span><span className="es-stat-v">{m.power}</span></span>}
                {m.level != null && <span className="es-stat"><span className="es-stat-l">{t("monsters.level")}</span><span className="es-stat-v">{m.level}</span></span>}
                {m.monsterType && <span className="es-stat"><span className="es-stat-l">{t("monsters.type")}</span><span className="es-stat-v">{m.monsterType}</span></span>}
              </div>
            </Link>
          ))}
        </div>
      )}

      {loading && <div className="es-state"><Loader2 className="es-spin" size={20} /> {t("common.loading")}</div>}
      {!loading && data.items.length < data.total && (
        <div className="es-loadmore-wrap">
          <button className="es-btn-secondary" data-testid="monsters-loadmore" onClick={() => setSkip(skip + PAGE)}>
            {t("common.showMore")}
          </button>
        </div>
      )}
    </div>
  );
}
